'use client';

import { ProviderInfo, ModelInfo } from '../../types/ai-settings';
import { motion } from 'framer-motion';

interface ModelConfiguratorProps {
  provider: ProviderInfo;
  selectedModel: string;
  onModelChange: (modelId: string) => void;
}

export function ModelConfigurator({
  provider,
  selectedModel,
  onModelChange,
}: ModelConfiguratorProps) {
  const formatTokenCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(0)}k`;
    }
    return count.toString();
  };

  const formatPrice = (price?: number): string => {
    if (!price) return 'N/A';
    return `$${price.toFixed(4)}`;
  };

  return (
    <div className="bg-white/5 rounded-lg p-4">
      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <span>🎯</span>
        Model Selection
      </h3>

      <div className="space-y-3">
        {provider.models.map((model) => {
          const isSelected = model.id === selectedModel;

          return (
            <motion.div
              key={model.id}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className={`relative rounded-lg border transition-all cursor-pointer ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              }`}
              onClick={() => onModelChange(model.id)}
            >
              <div className="p-4">
                {/* Model Header */}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      {model.name}
                      {model.deprecated && (
                        <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                          Deprecated
                        </span>
                      )}
                    </h4>
                    {model.description && (
                      <p className="text-xs text-white/60 mt-1">
                        {model.description}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <span className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </span>
                  )}
                </div>

                {/* Model Stats */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <span className="text-xs text-white/40">Context Window</span>
                    <p className="text-sm text-white/80 font-mono">
                      {formatTokenCount(model.contextWindow)} tokens
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-white/40">Max Output</span>
                    <p className="text-sm text-white/80 font-mono">
                      {formatTokenCount(model.maxTokens)} tokens
                    </p>
                  </div>
                </div>

                {/* Pricing */}
                {model.pricing && (
                  <div className="mb-3 p-2 bg-white/5 rounded">
                    <span className="text-xs text-white/40 block mb-1">Pricing (per 1k tokens)</span>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-white/60">Input:</span>
                        <span className="text-white/80 font-mono ml-1">
                          {formatPrice(model.pricing.input)}
                        </span>
                      </div>
                      <div>
                        <span className="text-white/60">Output:</span>
                        <span className="text-white/80 font-mono ml-1">
                          {formatPrice(model.pricing.output)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {Object.entries(model.supports).map(([feature, supported]) => {
                    if (!supported) return null;
                    
                    const featureIcons: Record<string, string> = {
                      chat: '💬',
                      completion: '📝',
                      functionCalling: '🔧',
                      vision: '👁️',
                      streaming: '⚡',
                      jsonMode: '📊',
                    };

                    return (
                      <span
                        key={feature}
                        className="text-xs px-2 py-1 bg-white/10 text-white/70 rounded flex items-center gap-1"
                      >
                        <span>{featureIcons[feature] || '✓'}</span>
                        <span className="capitalize">
                          {feature.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </span>
                    );
                  })}
                </div>

                {/* Recommended For */}
                {model.recommendedFor && model.recommendedFor.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <span className="text-xs text-white/40">Recommended for:</span>
                    <p className="text-xs text-white/60 mt-1">
                      {model.recommendedFor.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {provider.models.length === 0 && (
        <div className="text-center py-8 text-white/40 text-sm">
          No models available for this provider
        </div>
      )}
    </div>
  );
}